import { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigation } from 'react-router-dom';

import {
  NAVIGATION_FEEDBACK_DELAY_MS,
  classifyNavigationFeedback,
  hasPendingWork,
  subscribePendingWork,
} from '../utils/navigationFeedback';

/**
 * ¿Hay una navegación en curso que merezca enseñarle algo al usuario?
 *
 * React Router no pinta nada mientras corren los `loader`: deja la pantalla
 * anterior montada y congelada. En Chrome eso dura milisegundos; en una tablet
 * con cobertura irregular son segundos, y el operario cree que la app se ha
 * colgado y vuelve a pulsar.
 *
 * - POST → `true` en el acto (hay una mutación corriendo).
 * - GET  → `true` solo si la navegación pasa de `delayMs`.
 * - Parado → `false`, y el reloj pendiente se cancela.
 *
 * Spec: docs/superpowers/specs/2026-08-14-feedback-navegacion-asistentes-design.md
 */
export const useIsNavigating = (
  delayMs: number = NAVIGATION_FEEDBACK_DELAY_MS
): boolean => {
  const navigation = useNavigation();
  const navigationMode = classifyNavigationFeedback(navigation);

  // Trabajo previo a la navegación (una consulta antes de `navigate()`). El
  // router sigue en `idle` durante ese rato, así que sin esto la pantalla se
  // queda muerta — ver `withNavigationFeedback`.
  const pendingWork = useSyncExternalStore(
    subscribePendingWork,
    hasPendingWork
  );

  // Se trata como una navegación GET: pasa por el mismo umbral, así que un
  // trabajo previo rápido tampoco parpadea.
  const mode: typeof navigationMode =
    navigationMode === 'none' && pendingWork ? 'delayed' : navigationMode;

  // La clave del destino: sin ella, dos navegaciones GET encadenadas dejarían
  // el `mode` en 'delayed' sin que el efecto se volviera a ejecutar, y el reloj
  // de la primera valdría para la segunda.
  const destinationKey = navigation.location?.key;

  const [delayElapsed, setDelayElapsed] = useState(false);

  useEffect(() => {
    if (mode !== 'delayed') {
      setDelayElapsed(false);
      return undefined;
    }

    const timer = setTimeout(() => setDelayElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [mode, destinationKey, delayMs]);

  return mode === 'immediate' || (mode === 'delayed' && delayElapsed);
};
